/**
 * MetricsLive — métricas de catalogador en producción (matching_process), por sitio.
 *
 * Endpoint:
 *   GET /api/qa/metrics/live?intake=X → { intake, bySitio: [{ sitio, matches, avg_score, high_conf, low_conf }] }
 *
 * Render:
 *   - Una card por sitio con: matches, avg_score, high_conf (>=90), low_conf (<70)
 *   - Barra horizontal CSS apilada (high / mid / low) sobre total. No usa libs de chart.
 */
import React, { useState, useCallback } from 'react';
import { API_BASE } from '../config';
import './MetricsLive.css';

function fmtNum(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('es-AR');
}

function fmtScore(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toFixed(1);
}

function SitioCard({ sitio, matches, avg_score, high_conf, low_conf }) {
  const total = Number(matches) || 0;
  const high  = Number(high_conf) || 0;
  const low   = Number(low_conf) || 0;
  const mid   = Math.max(0, total - high - low);

  const pctHigh = total ? (high / total) * 100 : 0;
  const pctMid  = total ? (mid  / total) * 100 : 0;
  const pctLow  = total ? (low  / total) * 100 : 0;

  return (
    <div className="qa-sitio-card">
      <div className="qa-sitio-header">
        <div className="qa-sitio-name">{sitio || '(sin sitio)'}</div>
        <div className="qa-sitio-count">{fmtNum(matches)} matches</div>
      </div>

      <div className="qa-sitio-stats">
        <div>
          <div className="qa-sitio-stat-label">Avg score</div>
          <div className="qa-sitio-stat-value">{fmtScore(avg_score)}</div>
        </div>
        <div>
          <div className="qa-sitio-stat-label">High conf (≥90)</div>
          <div className="qa-sitio-stat-value">{fmtNum(high_conf)}</div>
          <div className="qa-sitio-stat-sub">{pctHigh.toFixed(1)}% del total</div>
        </div>
      </div>

      <div className="qa-progress-label">
        <span>Distribución</span>
        <span className="qa-progress-label-value">{pctHigh.toFixed(1)}% high</span>
      </div>
      <div className="qa-progress-track">
        <div className="qa-progress-segment-high" style={{ width: `${pctHigh}%` }} />
        <div className="qa-progress-segment-mid"  style={{ width: `${pctMid}%` }} />
        <div className="qa-progress-segment-low"  style={{ width: `${pctLow}%` }} />
      </div>
      <div className="qa-progress-legend">
        <span>
          <span className="qa-progress-legend-dot" style={{ background: '#10b981' }} />
          high (≥90): {fmtNum(high)}
        </span>
        <span>
          <span className="qa-progress-legend-dot" style={{ background: '#f59e0b' }} />
          mid (70-89): {fmtNum(mid)}
        </span>
        <span>
          <span className="qa-progress-legend-dot" style={{ background: '#ef4444' }} />
          low (&lt;70): {fmtNum(low)}
        </span>
      </div>
    </div>
  );
}

export default function MetricsLive() {
  const [intake,  setIntake]  = useState('');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handleLoad = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = intake.trim() ? `?intake=${encodeURIComponent(intake.trim())}` : '';
      const res = await fetch(`${API_BASE}/api/qa/metrics/live${qs}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Sin permisos.');
      }
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(e.message || 'Error al cargar métricas');
    } finally {
      setLoading(false);
    }
  }, [intake]);

  const bySitio = Array.isArray(data?.bySitio) ? data.bySitio : [];
  const totalMatches = bySitio.reduce((acc, s) => acc + (Number(s.matches) || 0), 0);

  return (
    <div className="um-card">
      <div className="qa-metrics-toolbar">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Intake
          </label>
          <input
            type="text"
            value={intake}
            onChange={(e) => setIntake(e.target.value)}
            placeholder="ej: toma_40 (vacío = todos)"
            onKeyDown={(e) => { if (e.key === 'Enter') handleLoad(); }}
          />
        </div>
        <button
          className="um-btn um-btn-primary"
          onClick={handleLoad}
          disabled={loading}
          style={{ marginBottom: 1 }}
        >
          {loading ? 'Cargando...' : 'Cargar'}
        </button>
      </div>

      {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

      {data && (
        <div className="qa-metrics-info">
          <strong>Intake:</strong> {data.intake || 'all'}{' · '}
          <strong>Sitios:</strong> {bySitio.length}{' · '}
          <strong>Total matches:</strong> {fmtNum(totalMatches)}
        </div>
      )}

      {!data && !error && !loading && (
        <div className="um-empty">
          Ingresá un intake (o dejá vacío para todos) y presioná Cargar.
        </div>
      )}

      {data && bySitio.length === 0 && (
        <div className="um-empty">Sin métricas para este intake.</div>
      )}

      {bySitio.length > 0 && (
        <div className="qa-metrics-grid">
          {bySitio.map(s => (
            <SitioCard
              key={s.sitio || '_no_sitio'}
              sitio={s.sitio}
              matches={s.matches}
              avg_score={s.avg_score}
              high_conf={s.high_conf}
              low_conf={s.low_conf}
            />
          ))}
        </div>
      )}
    </div>
  );
}
